import { useCallback, useEffect, useRef, useState } from "react";
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  UploadIcon,
} from "@heroicons/react/outline";
import PageLoader from "../../components/pageLoader";
import Toast from "../../components/toast";
import ToastContainer from "../../components/toastContainer";
import { useNavigate } from "react-router-dom";
import {
  createPayee,
  deletePayee,
  getAllPayees,
  getPayees,
  updatePayee,
} from "../../api/payee";
import DeleteConfirmationModal from "../../components/deleteModal";
import {
  createCompanyExpenses,
  deleteCompanyExpenses,
  getCompanyExpenses,
  updateCompanyExpenses,
} from "../../api/expenses";
import CompanyExpensesFormModal from "./CompanyExpensesModal";
import { getAllCompanies } from "../../api/companiesApi";

const CompanyExpensesPage = () => {
  const [showAddModal, setShowAddModal] = useState(false);

  const LIMIT = 10;

  const [expenses, setExpenses] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [deleteExpensesId, setDeleteExpensesId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const totalPages = Math.ceil(total / LIMIT);

  const [editingExpenses, setEditingExpenses] = useState(null);
  const [saving, setSaving] = useState(false);

  const [status, setStatus] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [proofFile, setProofFile] = useState(null);
  const [proofDragOver, setProofDragOver] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const proofInputRef = useRef(null);

  const [toast, setToast] = useState(null);

  const [allCompanies, setAllCompanies] = useState([]);
  const [allPayees, setAllPayees] = useState([]);

  const [scrollState, setScrollState] = useState({ left: false, right: false });
  const wrapperRef = useRef(null);

  const statusStyles = {
    paid: "bg-emerald-500/10 text-emerald-400",
    awaiting: "bg-amber-500/10 text-amber-400",
    on_hold: "bg-red-500/10 text-red-400",
  };

  const statusClass = (s) =>
    statusStyles[(s || "").toLowerCase()] || "bg-slate-500/10 text-slate-400";

  const navigate = useNavigate();

  const getPageNumbers = () =>
    Array.from({ length: totalPages }, (_, i) => i + 1);

  // ── Scroll detection ──
  const updateScrollState = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const left = el.scrollLeft > 2;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    setScrollState((prev) =>
      prev.left === left && prev.right === right ? prev : { left, right },
    );
  }, []);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const raf = requestAnimationFrame(() => updateScrollState());
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [expenses, updateScrollState]);

  const fetchCompanyExpenses = async () => {
    try {
      setLoading(true);
      setError("");
      const offset = (page - 1) * LIMIT;
      const data = await getCompanyExpenses(LIMIT, offset);
      setExpenses(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      setError("Failed to load company expenses.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const data = await getAllCompanies();
      setAllCompanies((data.results || []).map((c) => ({ id: c.id, name: c.name })));
    } catch (err) {}
  };

  const fetchAllPayees = async () => {
    try {
      const data = await getAllPayees();
      setAllPayees((data.results || []).map((p) => ({ id: p.id, name: p.name })));
    } catch (err) {}
  };

  const handleExpensesSubmit = async (info) => {
    try {
      setSaving(true);

      const formData = new FormData();
      if (info.source_company && info.source_company !== "personal") {
        formData.append("source_company", info.source_company);
      }
      formData.append("payee", info.payee);
      formData.append("invoice_no", info.invoice_no || "");
      formData.append("amount_no_vat", info.amount);
      formData.append("vat", info.vat);
      formData.append("currency", info.currency);
      formData.append("type_of_payment", info.payment_type);
      formData.append("comments", info.comments || "");
      if (info.file) {
        formData.append("invoice_file", info.file);
      }

      if (editingExpenses) {
        await updateCompanyExpenses(editingExpenses.id, formData);
      } else {
        await createCompanyExpenses(formData);
      }

      setEditingExpenses(null);
      setShowAddModal(false);
      fetchCompanyExpenses();
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExpenses = async () => {
    try {
      setDeleting(true);
      await deleteCompanyExpenses(deleteExpensesId);
      setDeleteExpensesId(null);
      fetchCompanyExpenses();
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusChange = async () => {
    try {
      setStatusSaving(true);
      const formData = new FormData();
      formData.append("status", newStatus);
      if (newStatus === "paid" && proofFile) {
        formData.append("proof_of_payment", proofFile);
      }
      await updateCompanyExpenses(status.id, formData);
      setStatus(null);
      setNewStatus("");
      setProofFile(null);
      fetchCompanyExpenses();
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setStatusSaving(false);
    }
  };

  // Reset proof file when status modal closes or status changes away from paid
  const handleStatusModalClose = () => {
    setStatus(null);
    setNewStatus("");
    setProofFile(null);
  };

  useEffect(() => {
    fetchAllPayees();
    fetchCompanies();
  }, []);

  useEffect(() => {
    fetchCompanyExpenses();
  }, [page]);

  if (loading) return <PageLoader />;

  return (
    <div className="grid grid-cols-1 gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Company Expenses</h1>
          <p className="text-sm text-slate-400">Create & Manage Company Expenses</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
        >
          <PlusIcon className="w-4 h-4" />
          Add Payment
        </button>
      </div>

      {/* Table */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl overflow-hidden">
        <div className="relative">
          {scrollState.left && (
            <div
              className="absolute left-0 top-0 bottom-0 w-10 z-10 pointer-events-none"
              style={{ background: "linear-gradient(to right, rgba(10,18,35,0.9), transparent)" }}
            />
          )}
          {scrollState.right && (
            <div
              className="absolute right-0 top-0 bottom-0 w-14 z-10 pointer-events-none"
              style={{ background: "linear-gradient(to left, rgba(10,18,35,0.95), transparent)" }}
            />
          )}
          <div
            ref={wrapperRef}
            className="w-full overflow-x-auto"
            style={{ scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,0.12) transparent" }}
          >
            <table className="border-collapse" style={{ minWidth: "1100px", width: "100%" }}>
              <thead>
                <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-center text-slate-400">
                  <th className="px-6 py-4 whitespace-nowrap">Payer</th>
                  <th className="px-6 py-4 whitespace-nowrap">Payee</th>
                  <th className="px-6 py-4 whitespace-nowrap">IBAN</th>
                  <th className="px-6 py-4 whitespace-nowrap">Invoice No</th>
                  <th className="px-6 py-4 whitespace-nowrap">Currency</th>
                  <th className="px-6 py-4 whitespace-nowrap">Amount</th>
                  <th className="px-6 py-4 whitespace-nowrap">Invoice Link</th>
                  <th className="px-6 py-4 whitespace-nowrap">Payment Type</th>
                  <th className="px-6 py-4 whitespace-nowrap">Comments</th>
                  <th className="px-6 py-4 whitespace-nowrap">Status</th>
                  <th className="px-6 py-4 whitespace-nowrap">Proof of Payment</th>
                  <th className="px-6 py-4 whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((py) => (
                  <tr
                    key={py.id}
                    className="border-b border-white/5 last:border-none text-center hover:bg-slate-800/40 transition"
                  >
                    <td className="px-6 py-4 text-sm text-slate-300 whitespace-nowrap">{py.source_company?.name ?? "—"}</td>
                    <td className="px-6 py-4 text-sm text-slate-300 whitespace-nowrap">{py.payee?.name ?? "—"}</td>
                    <td className="px-6 py-4 text-sm text-slate-300 whitespace-nowrap font-mono">{py.payee?.iban ?? "—"}</td>
                    <td className="px-6 py-4 text-sm text-slate-300 whitespace-nowrap">{py.invoice_no ?? "—"}</td>
                    <td className="px-6 py-4 text-sm text-slate-300 whitespace-nowrap">{py.currency}</td>
                    <td className="px-6 py-4 text-sm text-slate-300 whitespace-nowrap">{py.total}</td>
                    <td className="px-6 py-4 text-center">
                      {py.invoice_file ? (
                        <button onClick={() => window.open(py.invoice_file, "_blank")} className="text-pink-500 hover:text-pink-400">
                          <DocumentTextIcon className="w-5 h-5 inline" />
                        </button>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-300 whitespace-nowrap">{py.type_of_payment ?? "—"}</td>
                    <td className="px-6 py-4 text-sm text-slate-300 max-w-[180px]">
                      <div className="truncate">{py.comments ?? "—"}</div>
                    </td>
                    <td className="px-3 py-4 text-sm text-slate-300">
                      <div className="flex items-center justify-center gap-1">
                        <span className={`px-2.5 py-0.5 rounded-md text-xs font-medium ${statusClass(py.status)}`}>
                          {py.status || "-"}
                        </span>
                        <button
                          onClick={() => { setStatus(py); setNewStatus(py.status || ""); setProofFile(null); }}
                          className="p-1 rounded text-slate-500 hover:text-white hover:bg-slate-700 transition"
                          title="Change status"
                        >
                          <ChevronDownIcon className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap">
                      {py.proof_of_payment ? (
                        <button onClick={() => window.open(py.proof_of_payment, "_blank")} className="text-emerald-500 hover:text-emerald-400">
                          <DocumentTextIcon className="w-5 h-5 inline" />
                        </button>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => setEditingExpenses(py)}
                          className="p-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-700 transition"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteExpensesId(py.id)}
                          className="p-2 rounded-md text-slate-300 hover:text-red-400 hover:bg-red-500/10 transition"
                          title="Delete expense"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {expenses.length === 0 && (
          <div className="py-10 text-center text-slate-400 text-sm">No expenses found.</div>
        )}

        {/* Pagination */}
        <div className="flex justify-end">
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 px-6 py-4 text-sm text-slate-400">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className={`px-3 py-1 rounded-md border border-white/10 transition ${page === 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-800 text-white"}`}
              >
                Previous
              </button>
              <div className="flex gap-1">
                {getPageNumbers().map((pageNumber) => (
                  <button
                    key={pageNumber}
                    onClick={() => setPage(pageNumber)}
                    className={`px-3 py-1 rounded-md border border-white/10 transition ${page === pageNumber ? "bg-pink-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`}
                  >
                    {pageNumber}
                  </button>
                ))}
              </div>
              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
                className={`px-3 py-1 rounded-md border border-white/10 transition ${page === totalPages ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-800 text-white"}`}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Status Change Modal ── */}
      {status && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-sm bg-slate-900 border border-white/10 rounded-xl shadow-xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-white/10">
              <h2 className="text-white font-semibold text-sm">Change Status</h2>
              <p className="text-xs text-slate-400 mt-0.5">{status.payee?.name ?? status.id}</p>
            </div>

            {/* Options */}
            <div className="px-6 py-4 space-y-2">
              {["awaiting", "paid", "on_hold"].map((s) => (
                <label
                  key={s}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition ${
                    newStatus === s
                      ? "border-pink-500/50 bg-pink-500/10"
                      : "border-white/10 hover:bg-slate-800"
                  }`}
                >
                  <input
                    type="radio"
                    name="status"
                    value={s}
                    checked={newStatus === s}
                    onChange={() => { setNewStatus(s); if (s !== "paid") setProofFile(null); }}
                    className="accent-pink-500"
                  />
                  <span className={`text-xs font-medium capitalize px-2.5 py-0.5 rounded-md ${statusClass(s)}`}>
                    {s.replace("_", " ")}
                  </span>
                </label>
              ))}

              {/* ── Proof of Payment upload — shown only when "paid" is selected ── */}
              {newStatus === "paid" && (
                <div className="mt-1 pt-3 border-t border-white/10">
                  <p className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-2">
                    Proof of Payment{" "}
                    <span className="text-slate-600 normal-case tracking-normal font-normal">(optional)</span>
                  </p>

                  {proofFile ? (
                    /* Selected file preview */
                    <div className="flex items-center justify-between bg-slate-800/60 border border-white/10 rounded-lg px-3 py-2.5">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-7 h-7 rounded-md bg-emerald-600/20 border border-emerald-500/20 flex items-center justify-center shrink-0">
                          <UploadIcon className="w-3.5 h-3.5 text-emerald-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs text-white truncate">{proofFile.name}</p>
                          <p className="text-[10px] text-slate-500">{(proofFile.size / 1024).toFixed(1)} KB</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setProofFile(null)}
                        className="ml-2 text-slate-500 hover:text-red-400 transition shrink-0"
                      >
                        <TrashIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    /* Drop zone */
                    <div
                      onDragOver={(e) => { e.preventDefault(); setProofDragOver(true); }}
                      onDragLeave={() => setProofDragOver(false)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setProofDragOver(false);
                        const f = e.dataTransfer.files?.[0];
                        if (f) setProofFile(f);
                      }}
                      onClick={() => proofInputRef.current?.click()}
                      className={`cursor-pointer border-2 border-dashed rounded-xl px-4 py-4 text-center transition ${
                        proofDragOver
                          ? "border-emerald-500/60 bg-emerald-500/5"
                          : "border-white/10 hover:border-white/20 hover:bg-slate-800/40"
                      }`}
                    >
                      <UploadIcon className="w-5 h-5 text-slate-500 mx-auto mb-1.5" />
                      <p className="text-xs text-slate-400">
                        Drop file or{" "}
                        <span className="text-emerald-400 underline underline-offset-2">browse</span>
                      </p>
                      <p className="text-[10px] text-slate-600 mt-0.5">PDF, PNG, JPG</p>
                      <input
                        ref={proofInputRef}
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg"
                        className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) setProofFile(f); }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
              <button
                onClick={handleStatusModalClose}
                className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleStatusChange}
                disabled={statusSaving || !newStatus}
                className="px-4 py-2 text-sm rounded-lg text-white bg-pink-600 hover:bg-pink-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                {statusSaving ? "Saving..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      <CompanyExpensesFormModal
        isOpen={showAddModal || !!editingExpenses}
        onClose={() => { setShowAddModal(false); setEditingExpenses(null); }}
        onSubmit={handleExpensesSubmit}
        loading={saving}
        mode={editingExpenses ? "edit" : "create"}
        initialData={editingExpenses}
        companies={allCompanies}
        payees={allPayees}
      />

      <DeleteConfirmationModal
        isOpen={!!deleteExpensesId}
        description="Are you sure you want to delete this expense. This action cannot be undone."
        onClose={() => setDeleteExpensesId(null)}
        onConfirm={handleDeleteExpenses}
        loading={deleting}
      />

      <ToastContainer>
        {toast && (
          <div className="toast-enter">
            <Toast
              type={toast.type}
              message={toast.message}
              onClose={() => setToast(null)}
            />
          </div>
        )}
      </ToastContainer>
    </div>
  );
};

export default CompanyExpensesPage;